import { IconX } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import PrimaryButton from "../../../components/PrimaryButton";
import InputField from "../../../components/common/InputField";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import NewSelectInput from "../../../components/common/FilterInputField";
import { useWarehouseListData } from "../../../apis/warehouseApi";
import { PAYMENT_TYPE_OPTIONS } from "../../../types/types";
import { useServiceAllListData } from "../../../apis/orderApis";
import { useIntegrationChannelsAll } from "../../../apis/integrationApis";
import {
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
  useDistrictFetchData,
} from "../../../apis/locationApis";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  onClose: () => void;
  setFilters: (filters: Array<Record<string, string>>) => void;
  currentFilters?: Array<Record<string, string>>;
  countries?: any[];
  originCities?: any[];
  receiverCities?: any[];
  receiverDistricts?: any[];
};

const ORDER_STATUS_OPTIONS = [
  { id: "NEW_ORDER", value: "NEW_ORDER", text: "New Order" },
  {
    id: "PICKUP_GENERATED",
    value: "PICKUP_GENERATED",
    text: "Pickup Generated",
  },
  {
    id: "READY_FOR_PICKUP",
    value: "READY_FOR_PICKUP",
    text: "Ready For Pickup",
  },
  {
    id: "PICKUP_COLLECTED",
    value: "PICKUP_COLLECTED",
    text: "Pickup Collected",
  },
  {
    id: "RECEIVE_IN_WAREHOUSE",
    value: "RECEIVE_IN_WAREHOUSE",
    text: "Received In Warehouse",
  },
  { id: "SHELVE", value: "SHELVE", text: "Shelve" },
  { id: "FORWARD_TO_3PL", value: "FORWARD_TO_3PL", text: "Forward To 3PL" },
  {
    id: "OUT_FOR_DELIVERY",
    value: "OUT_FOR_DELIVERY",
    text: "Out For Delivery",
  },
  {id: "REVERSE_NEW", value: "REVERSE_NEW", text: "Reverse New"},
  { id: "IN_TRANSIT", value: "IN_TRANSIT", text: "In Transit" },
  { id: "DELIVERED", value: "DELIVERED", text: "Delivered" },
  { id: "NOT_DELIVERED", value: "NOT_DELIVERED", text: "Not Delivered" },
  { id: "RETURNED", value: "RETURNED", text: "Returned" },
  { id: "CANCELLED", value: "CANCELLED", text: "Cancelled" },
  { id: "DELETED", value: "DELETED", text: "Deleted" },
  { id: "PENDING", value: "PENDING", text: "Pending" },
  {
    id: "RETURN_TO_SHIPPER",
    value: "RETURN_TO_SHIPPER",
    text: "Return To Shipper",
  },
];

const initializeRawFilters = (
  currentFilters?: Array<Record<string, string>>,
  countries?: any[],
  originCities?: any[],
  receiverCities?: any[],
  receiverDistricts?: any[]
) => {
  const defaultFilters = {
    searchFilter: "",
    seller: "",
    warehouseName: "",
    paymentType: "",
    codAmount: "",
    sameDate: "",
    entryDate: "",
    closeDate: "",
    senderCountryId: "",
    senderCityId: "",
    senderCityName: "",
    receiverCountryId: "",
    receiverCityId: "",
    receiverCityName: "",
    city: "",
    serviceType: "",
    platform: "",
    shippingCompany: "",
    shippingAwbNumber: "",
    district: "",
    entryDateFrom: "",
    entryDateTo: "",
    closeDateFrom: "",
    closeDateTo: "",
    status: "",
  };

  if (!currentFilters || currentFilters.length === 0) {
    return defaultFilters;
  }

  const result = { ...defaultFilters };

  currentFilters.forEach((filter) => {
    const [key, value] = Object.entries(filter)[0] || [];
    if (!key || !value) return;

    if (
      key === "awbNumber" ||
      key === "referenceNumber" ||
      key === "mobileNumber"
    ) {
      if (!result.searchFilter) {
        result.searchFilter = value;
      } else if (!result.searchFilter.includes(value)) {
        result.searchFilter += `, ${value}`;
      }
    } else if (key === "senderCountryName" && countries) {
      const country = countries.find((c: any) => c.name === value);
      if (country) result.senderCountryId = String(country.id);
    } else if (key === "senderCityName" && originCities) {
      const city = originCities.find((c: any) => c.name === value);
      if (city) result.senderCityId = String(city.id);
      result.senderCityName = value;
    } else if (key === "receiverCountryName" && countries) {
      const country = countries.find((c: any) => c.name === value);
      if (country) result.receiverCountryId = String(country.id);
    } else if (key === "receiverCityName" && receiverCities) {
      const city = receiverCities.find((c: any) => c.name === value);
      if (city) result.receiverCityId = String(city.id);
      result.receiverCityName = value;
    } else if (key === "receiverDistrictName") {
      result.district = value;
    } else if (key === "orderType") {
      result.serviceType = value;
    } else if (key === "status") {
      result.status = value;
    } else {
      result[key as keyof typeof result] = value;
    }
  });

  return result;
};

export default function FilterSidebar({
  onClose,
  setFilters,
  currentFilters,
  countries: propsCountries,
  originCities: propsOriginCities,
  receiverCities: propsReceiverCities,
  receiverDistricts: propsReceiverDistricts,
}: Props) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("All");

  // Location data hooks
  const { data: countries } = useFetchCountriesAllData();
  const { data: apiOriginCities } = useFetchCountryCitiesAllData(
    "",
    "",
    "short"
  );
  const { data: apiReceiverCities } = useFetchCountryCitiesAllData(
    "",
    "",
    "short"
  );
  const { data: apiReceiverDistricts } = useDistrictFetchData("", "", "short");

  // Use provided data or fetch from APIs
  const countriesData = propsCountries || countries;
  const originCitiesData = propsOriginCities || apiOriginCities;
  const receiverCitiesData = propsReceiverCities || apiReceiverCities;
  const receiverDistrictsData = propsReceiverDistricts || apiReceiverDistricts;

  const [rawFilters, setRawFilters] = useState(() =>
    initializeRawFilters(
      currentFilters,
      countriesData,
      originCitiesData,
      receiverCitiesData,
      receiverDistrictsData
    )
  );

  // Re-initialize when currentFilters change
  useEffect(() => {
    setRawFilters(
      initializeRawFilters(
        currentFilters,
        countriesData,
        originCitiesData,
        receiverCitiesData,
        receiverDistrictsData
      )
    );
  }, [
    currentFilters,
    countriesData,
    originCitiesData,
    receiverCitiesData,
    receiverDistrictsData,
  ]);

  const tabs = ["All", "Details", "Date", "Location", "Order"];

  const handleChange = (key: string, value: string) => {
    setRawFilters((prev: any) => ({ ...prev, [key]: value }));
  };
  const handleApplyFilters = () => {
    const formattedFilters = Object.entries(rawFilters)
      .filter(([_, value]) => value.trim() !== "")
      .reduce((acc: any[], [key, value]) => {
        let mappedKey = key === "serviceType" ? "orderType" : key;

        if (key === "searchFilter") {
          const parts = String(value)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

          parts.forEach((p) => {
            acc.push({ awbNumber: p });
            acc.push({ referenceNumber: p });
            acc.push({ mobileNumber: p });
          });
        } else if (key === "senderCountryId") {
          const selectedCountry = countriesData?.find(
            (c: any) => String(c.id) === String(value)
          );
          if (selectedCountry?.name) {
            acc.push({ senderCountryName: selectedCountry.name });
          }
        } else if (key === "senderCityId") {
          const selectedCity = originCities?.find(
            (c: any) => String(c.id) === String(value)
          );
          if (selectedCity?.name) {
            acc.push({ senderCityName: selectedCity.name });
          }
        } else if (key === "receiverCountryId") {
          const selectedCountry = countriesData?.find(
            (c: any) => String(c.id) === String(value)
          );
          if (selectedCountry?.name) {
            acc.push({ receiverCountryName: selectedCountry.name });
          }
        } else if (key === "receiverCityId") {
          const selectedCity = receiverCities?.find(
            (c: any) => String(c.id) === String(value)
          );
          if (selectedCity?.name) {
            acc.push({ receiverCityName: selectedCity.name });
          }
        } else if (key === "district" && value) {
          acc.push({ receiverDistrictName: value });
        } else if (key === "entryDate" && value) {
          acc.push({ entryDate: value });
        } else if (key === "closeDate" && value) {
          acc.push({ closeDate: value });
        } else if (
          key === "entryDateFrom" ||
          key === "entryDateTo" ||
          key === "closeDateFrom" ||
          key === "closeDateTo"
        ) {
          // Skip these here, we'll add them outside the loop
          return acc;
        } else if (key !== "senderCityName" && key !== "receiverCityName") {
          acc.push({ [mappedKey]: value });
        }

        return acc;
      }, [] as any[]);

    // Add date range filters outside the loop to avoid duplicates
    if (rawFilters.entryDateFrom?.trim()) {
      formattedFilters.push({ entryDateFrom: rawFilters.entryDateFrom.trim() });
    }
    if (rawFilters.entryDateTo?.trim()) {
      formattedFilters.push({ entryDateTo: rawFilters.entryDateTo.trim() });
    }
    if (rawFilters.closeDateFrom?.trim()) {
      formattedFilters.push({ closeDateFrom: rawFilters.closeDateFrom.trim() });
    }
    if (rawFilters.closeDateTo?.trim()) {
      formattedFilters.push({ closeDateTo: rawFilters.closeDateTo.trim() });
    }

    setFilters(formattedFilters);
    onClose();
  };
  // Dropdown data Api call
  const { data: clients } = useMerchantAllListData();

  const { data: warehouses } = useWarehouseListData();

  const { data: serviceData } = useServiceAllListData();
  const { data: salesPlatforms } = useIntegrationChannelsAll("sales");
  const { data: shippingPlatforms } = useIntegrationChannelsAll("shipping");

  // Dependent location hooks based on selected country/city
  const { data: originCities } = useFetchCountryCitiesAllData(
    rawFilters.senderCountryId as string | undefined,
    "",
    "short"
  );
  const { data: receiverCities } = useFetchCountryCitiesAllData(
    rawFilters.receiverCountryId as string | undefined,
    "",
    "short"
  );
  const { data: receiverDistricts } = useDistrictFetchData(
    rawFilters.receiverCityId as string | undefined,
    "",
    "short"
  );

  useEffect(() => {
    if (!currentFilters || currentFilters.length === 0) return;

    const updates: any = {};

    const countryName = currentFilters.find(
      (f) => f.receiverCountryName
    )?.receiverCountryName;
    if (countryName && countriesData && !rawFilters.receiverCountryId) {
      const country = countriesData.find((c: any) => c.name === countryName);
      if (country) updates.receiverCountryId = String(country.id);
    }

    const cityName = currentFilters.find(
      (f) => f.receiverCityName
    )?.receiverCityName;
    if (
      cityName &&
      receiverCities &&
      receiverCities.length > 0 &&
      !rawFilters.receiverCityId
    ) {
      const city = receiverCities.find((c: any) => c.name === cityName);
      if (city) {
        updates.receiverCityId = String(city.id);
        updates.receiverCityName = city.name;
      }
    }

    const districtName = currentFilters.find(
      (f) => f.receiverDistrictName
    )?.receiverDistrictName;
    if (districtName && !rawFilters.district) {
      updates.district = districtName;
    }

    const senderCountryName = currentFilters.find(
      (f) => f.senderCountryName
    )?.senderCountryName;
    if (senderCountryName && countriesData && !rawFilters.senderCountryId) {
      const country = countriesData.find(
        (c: any) => c.name === senderCountryName
      );
      if (country) updates.senderCountryId = String(country.id);
    }

    const senderCityName = currentFilters.find(
      (f) => f.senderCityName
    )?.senderCityName;
    if (
      senderCityName &&
      originCities &&
      originCities.length > 0 &&
      !rawFilters.senderCityId
    ) {
      const city = originCities.find((c: any) => c.name === senderCityName);
      if (city) {
        updates.senderCityId = String(city.id);
        updates.senderCityName = city.name;
      }
    }

    if (Object.keys(updates).length > 0) {
      setRawFilters((prev) => ({ ...prev, ...updates }));
    }
  }, [
    currentFilters,
    countriesData,
    receiverCities,
    originCities,
    receiverDistricts,
    rawFilters.district,
    rawFilters.receiverCityId,
    rawFilters.receiverCountryId,
    rawFilters.senderCountryId,
    rawFilters.senderCityId,
  ]);

  return (
    <div className="h-full w-full bg-white rounded-l-3xl flex flex-col justify-between">
      {/* Header */}
      <div>
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">{t("Filter Settings")}</h2>
          <button
            onClick={onClose}
            className="bg-[#F7F6F2] rounded-full p-1 cursor-pointer"
          >
            <IconX size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 p-4 overflow-x-auto sticky top-14 z-50 bg-white">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`text-xs w-full font-medium px-3 py-1.5 rounded-full border ${
                activeTab === tab
                  ? "bg-black text-white border-black"
                  : "text-[#1E1E1E] border-[#DADADA] bg-[#F7F6F2]"
              }`}
            >
              {t(tab)}
            </button>
          ))}
        </div>

        {/* Filter Form */}
        <div className="px-6 space-y-4 pb-6">
          {(activeTab === "All" || activeTab === "Details") && (
            <>
              <h2 className="text-[#212121]/70 text-sm font-medium">{t("Details")}</h2>
              <InputField
                label={t("AWB No. (Optional)")}
                placeholder={t("Enter AWB, Reference Number,  or Mobile Number (comma-separated for multiple)")}
                value={rawFilters.searchFilter}
                onChange={(e) => handleChange("searchFilter", e.target.value)}
              />
              <NewSelectInput
                label={t("Status")}
                name="status"
                placeholder={t("Select Status")}
                value={rawFilters.status}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  handleChange("status", String(newValue));
                }}
                selectOptions={ORDER_STATUS_OPTIONS.map((opt) => ({
                  ...opt,
                  text: t(opt.text),
                }))}
              />
              <NewSelectInput
                label={t("Seller")}
                name="seller"
                placeholder={t("Select Seller")}
                value={rawFilters.seller}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  handleChange("seller", String(newValue));
                }}
                selectOptions={
                  clients?.map((client: any) => ({
                    id: client?.id,
                    value: client?.name,
                    text: client?.name,
                  })) || []
                }
              />
              <NewSelectInput
                name="warehouseId"
                label={t("Warehouse")}
                placeholder={t("Select Warehouse")}
                value={rawFilters.warehouseName}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  handleChange("warehouseName", String(newValue));
                }}
                selectOptions={
                  warehouses?.warehouses?.map((w: any) => ({
                    id: w?.id,
                    value: w?.name,
                    text: w?.name,
                  })) || []
                }
              />

              <NewSelectInput
                label={t("Payment Mode")}
                name="paymentType"
                placeholder={t("Select Payment Mode")}
                value={rawFilters.paymentType}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  handleChange("paymentType", String(newValue));
                }}
                selectOptions={PAYMENT_TYPE_OPTIONS.map((opt) => ({
                  ...opt,
                  text: t(opt.text),
                }))}
              />
              <InputField
                label={t("COD Amount")}
                placeholder={t("0.5 SAR")}
                value={rawFilters.codAmount}
                onChange={(e) => handleChange("codAmount", e.target.value)}
              />
            </>
          )}

          {(activeTab === "All" || activeTab === "Date") && (
            <>
              {/* <h2 className="text-[#212121]/70 text-sm font-medium">Date</h2> */}
              <InputField
                type="date"
                label={t("Same Date")}
                placeholder={""}
                value={rawFilters.sameDate}
                onChange={(e) => handleChange("sameDate", e.target.value)}
              />
              {/* <InputField
                type="date"
                label="Entry Date"
                placeholder={""}
                value={rawFilters.entryDate}
                onChange={(e) => handleChange("entryDate", e.target.value)}
              /> */}
              {/* Entry Date Range */}
              <div className="space-y-3">
                <h3 className="text-xs font-medium text-gray-600">
                  {t("Entry Date Range")}
                </h3>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <InputField
                      type="date"
                      label={t("From")}
                      value={rawFilters.entryDateFrom || ""}
                      onChange={(e) =>
                        handleChange("entryDateFrom", e.target.value)
                      }
                      max={rawFilters.entryDateTo || undefined}
                    />
                  </div>
                  <span className="self-end pb-2 text-gray-500 font-medium">
                    →
                  </span>
                  <div className="flex-1">
                    <InputField
                      type="date"
                      label={t("To")}
                      value={rawFilters.entryDateTo || ""}
                      onChange={(e) =>
                        handleChange("entryDateTo", e.target.value)
                      }
                      min={rawFilters.entryDateFrom || undefined}
                    />
                  </div>
                </div>
              </div>
              {/* Close Date Range */}
              <div className="space-y-3">
                <h3 className="text-xs font-medium text-gray-600">
                  {t("Close Date Range")}
                </h3>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <InputField
                      type="date"
                      label={t("From")}
                      value={rawFilters.closeDateFrom || ""}
                      onChange={(e) =>
                        handleChange("closeDateFrom", e.target.value)
                      }
                      max={rawFilters.closeDateTo || undefined}
                    />
                  </div>
                  <span className="self-end pb-2 text-gray-500 font-medium">
                    →
                  </span>
                  <div className="flex-1">
                    <InputField
                      type="date"
                      label={t("To")}
                      value={rawFilters.closeDateTo || ""}
                      onChange={(e) =>
                        handleChange("closeDateTo", e.target.value)
                      }
                      min={rawFilters.closeDateFrom || undefined}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          {(activeTab === "All" || activeTab === "Location") && (
            <>
              <h2 className="text-[#212121]/70 text-sm font-medium">
                {t("Location")}
              </h2>
              {/* Origin: Country -> City -> District (dependent dropdowns) */}
              {/* Countries */}
              <NewSelectInput
                label={t("Origin Country")}
                name="senderCountry"
                placeholder={t("Select Country")}
                value={rawFilters.senderCountryId}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  setRawFilters((prev: any) => ({
                    ...prev,
                    senderCountryId: String(newValue),
                    senderCityId: "",
                    senderCityName: "",
                    district: "",
                  }));
                }}
                selectOptions={
                  (countries || [])?.map((c: any) => ({
                    id: c?.id,
                    value: c?.id,
                    text: c?.name,
                  })) || []
                }
              />

              {/* Cities for selected country */}
              <NewSelectInput
                label={t("Origin City")}
                name="senderCity"
                placeholder={t("Select City")}
                value={rawFilters.senderCityId}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  const selectedCity = (originCities || []).find(
                    (c: any) => String(c.id) === String(newValue)
                  );
                  setRawFilters((prev: any) => ({
                    ...prev,
                    senderCityId: String(newValue),
                    senderCityName: selectedCity?.name || "",
                    district: "",
                  }));
                }}
                selectOptions={
                  (originCities || []).map((city: any) => ({
                    id: city?.id,
                    value: city?.id,
                    text: city?.name,
                  })) || []
                }
              />

              {/* Destination: Country -> City -> District */}
              <NewSelectInput
                label={t("Destination Country")}
                name="receiverCountry"
                placeholder={t("Select Country")}
                value={rawFilters.receiverCountryId}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  setRawFilters((prev: any) => ({
                    ...prev,
                    receiverCountryId: String(newValue),
                    receiverCityId: "",
                    receiverCityName: "",
                    district: "",
                  }));
                }}
                selectOptions={
                  (countries || [])?.map((c: any) => ({
                    id: c?.id,
                    value: c?.id,
                    text: c?.name,
                  })) || []
                }
              />

              <NewSelectInput
                label={t("Destination City")}
                name="receiverCity"
                placeholder={t("Select City")}
                value={rawFilters.receiverCityId}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  const selectedCity = (receiverCities || []).find(
                    (c: any) => String(c.id) === String(newValue)
                  );
                  setRawFilters((prev: any) => ({
                    ...prev,
                    receiverCityId: String(newValue),
                    receiverCityName: selectedCity?.name || "",
                    district: "",
                  }));
                }}
                selectOptions={
                  (receiverCities || []).map((city: any) => ({
                    id: city?.id,
                    value: city?.id,
                    text: city?.name,
                  })) || []
                }
              />

              <NewSelectInput
                label={t("Destination District")}
                name="receiverDistrict"
                placeholder={t("Select District")}
                value={rawFilters.district}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  handleChange("district", String(newValue));
                }}
                selectOptions={
                  (receiverDistricts || []).map((d: any) => ({
                    id: d?.id,
                    value: d?.name,
                    text: d?.name,
                  })) || []
                }
              />

              {/* Districts for selected city */}
              {/* <NewSelectInput
                label="District"
                name="district"
                placeholder="Select District"
                value={rawFilters.district}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  handleChange("district", String(newValue));
                }}
                selectOptions={
                  (originDistricts || []).map((d: any) => ({
                    id: d?.id,
                    value: d?.name,
                    text: d?.name,
                  })) || []
                }
              /> */}
            </>
          )}

          {(activeTab === "All" || activeTab === "Order") && (
            <>
              <h2 className="text-[#212121]/70 text-sm font-medium">
                {t("Order Info")}
              </h2>
              <NewSelectInput
                label={t("Service Type")}
                name="serviceType"
                placeholder={t("Select Order Type")}
                value={rawFilters.serviceType}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  handleChange("serviceType", String(newValue));
                }}
                selectOptions={
                  serviceData?.map((service: any) => ({
                    id: service?.id,
                    value: service?.name,
                    text: service?.name,
                  })) || []
                }
              />
              <NewSelectInput
                label={t("Platform")}
                name="platform"
                placeholder={t("Select Platform")}
                value={rawFilters.platform}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  // Store platform company name
                  handleChange("platform", String(newValue));
                }}
                selectOptions={
                  salesPlatforms?.map((channel: any) => ({
                    id: channel?.id,
                    value: channel?.companyName,
                    text: channel?.companyName,
                  })) || []
                }
              />

              <NewSelectInput
                label={t("Shipping Company")}
                name="shippingCompany"
                placeholder={t("Select Shipping Company")}
                value={rawFilters.shippingCompany}
                onChange={(valOrEvent: any) => {
                  const newValue =
                    (valOrEvent &&
                      valOrEvent.target &&
                      valOrEvent.target.value) ??
                    (valOrEvent && valOrEvent.value) ??
                    valOrEvent ??
                    "";
                  handleChange("shippingCompany", String(newValue));
                }}
                selectOptions={
                  shippingPlatforms?.map((channel: any) => ({
                    id: channel?.id,
                    value: channel?.companyName,
                    text: channel?.companyName,
                  })) || []
                }
              />

              {/* <InputField
                label="Shipping AWB"
                placeholder="AWB-XXXXXX"
                value={rawFilters.shippingAwbNumber}
                onChange={(e) =>
                  handleChange("shippingAwbNumber", e.target.value)
                }
              /> */}
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-2 bg-white sticky bottom-0">
        <PrimaryButton onClick={handleApplyFilters} label={t("Apply Filters")} />
      </div>
    </div>
  );
}
